// Import necessary React and Material-UI components
// React is our main library for building user interfaces
// Material-UI provides pre-built components with a modern design
import React, { useState, useEffect, useMemo } from 'react';
import {
  // Layout components for structuring the page
  Box,  // A flexible container for layout
  Grid, // Creates a responsive grid system
  // Typography components for text display
  Typography, // Consistent text styling across the app
  // Interactive components
  Button,     // Clickable buttons for actions
  Dialog,     // Modal window for forms
  DialogTitle,
  DialogContent,
  DialogActions,
  // Form components
  TextField,   // Text input fields
  MenuItem,    // Dropdown menu items
  // Data display components
  IconButton,  // Buttons with icons
  Table,       // Structured data display
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,      // Elevation and background
  Card,       // Container with shadow
  CardContent,
  // Visual feedback components
  Chip,       // Compact elements for status
  Avatar,     // User profile pictures
  AvatarGroup, // Group of avatars
  FormControlLabel, // Label for form controls
  Checkbox,    // Boolean input
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
  ListItemText,
  OutlinedInput,
  Alert,
  InputAdornment,
  LinearProgress,
  Menu,
  ListItemIcon,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

// Import Material Icons for visual indicators
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PersonIcon from '@mui/icons-material/Person';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SearchIcon from '@mui/icons-material/Search';
import TaskIcon from '@mui/icons-material/Task';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import WarningIcon from '@mui/icons-material/Warning';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';

// Import styling utilities from Material-UI
import { styled } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import CircularProgress from '@mui/material/CircularProgress';
import { supabase } from '../../../lib/supabase';

// TypeScript interface defining the structure of a Task
// This helps catch errors and provides code completion
interface Task {
  id: string;           // Unique identifier for the task
  title: string;        // Task name
  description: string;  // Detailed explanation
  category: string;     // Type of task (e.g., Crop Management)
  priority: 'low' | 'medium' | 'high' | 'urgent';  // Task urgency level
  status: 'pending' | 'in-progress' | 'completed' | 'on-hold';  // Current state
  frequency: 'daily' | 'weekly' | 'monthly';  // How often task repeats
  timeSlots?: string[];  // Optional specific times for task
  isMandatory: boolean;  // Whether task is required
  dueDate: string;      // When task needs to be completed
  // Who is assigned to the task
  assignedTo: {
    id: string;
    name: string;
    avatar?: string;  // Optional profile picture
  }[];
  // Sub-tasks or steps to complete
  checklist: {
    id: string;
    text: string;
    completed: boolean;
  }[];
  notes: string;      // Additional information
  createdAt: string;  // When task was created
  updatedAt: string;  // Last modification time
  assignedWorkers: string[]; // IDs of assigned workers
  completedDate?: string;
}

// Interface for a Worker (simplified from the Worker component)
interface Worker {
  id: string;
  name: string;
  role: string;
  specialization: string[];
}

// Props interface for the FarmTasks component
interface FarmTasksProps {
  farmId: string;  // Unique identifier for the farm
  workers?: Worker[]; // Pass workers from parent component
  tutorialMode?: boolean;
  tutorialTasks?: Task[];
  tutorialMessage?: string;
}

// Available categories for tasks
const taskCategories = [
  'Crop Management',
  'Animal Care',
  'Equipment Maintenance',
  'Infrastructure',
  'General',
];

// Priority levels with their display properties
const taskPriorities = [
  { value: 'low', label: 'Low', color: 'info' },
  { value: 'medium', label: 'Medium', color: 'warning' },
  { value: 'high', label: 'High', color: 'error' },
  { value: 'urgent', label: 'Urgent', color: 'error' },
] as const;

// Status options with their display properties
const taskStatuses = [
  { value: 'pending', label: 'Pending', color: 'default' },
  { value: 'in-progress', label: 'In Progress', color: 'info' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'on-hold', label: 'On Hold', color: 'warning' },
] as const;

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
  borderRadius: '16px',
}));

const TaskPriorityIcon = styled(PriorityHighIcon, {
  shouldForwardProp: (prop) => prop !== 'priority',
})<{ priority: Task['priority'] }>(({ theme, priority }) => ({
  color: priority === 'high' ? theme.palette.error.main :
         priority === 'medium' ? theme.palette.warning.main :
         theme.palette.info.main,
}));

const StatusChip = styled(Chip)<{ status?: string }>(({ theme, status }) => {
  let color = theme.palette.text.secondary;
  let bg = theme.palette.action.selected;

  switch (status) {
    case 'completed':
      color = theme.palette.success.main;
      bg = alpha(theme.palette.success.main, 0.1);
      break;
    case 'in-progress':
      color = theme.palette.info.main;
      bg = alpha(theme.palette.info.main, 0.1);
      break;
    case 'on-hold':
      color = theme.palette.warning.main;
      bg = alpha(theme.palette.warning.main, 0.1);
      break;
    case 'pending':
      color = theme.palette.text.secondary;
      bg = theme.palette.action.hover;
      break;
  }

  return {
    color: color,
    backgroundColor: bg,
    fontWeight: 600,
    borderRadius: '8px',
  };
});

const PriorityChip = styled(Chip)<{ priority?: string }>(({ theme, priority }) => {
  let color = theme.palette.text.secondary;
  let bg = theme.palette.action.hover;

  switch (priority) {
    case 'urgent':
      color = theme.palette.error.dark;
      bg = alpha(theme.palette.error.main, 0.2);
      break;
    case 'high':
      color = theme.palette.error.main;
      bg = alpha(theme.palette.error.main, 0.1);
      break;
    case 'medium':
      color = theme.palette.warning.main;
      bg = alpha(theme.palette.warning.main, 0.1);
      break;
    case 'low':
      color = theme.palette.info.main;
      bg = alpha(theme.palette.info.main, 0.1);
      break;
  }

  return {
    color: color,
    backgroundColor: bg,
    fontWeight: 600,
    borderRadius: '6px',
    height: '24px',
    fontSize: '0.75rem',
  };
});

// Helper function to convert task to snake_case for Supabase
const toSnakeCaseTask = (task: any) => {
  return {
    title: task.title,
    description: task.description,
    category: task.category,
    priority: task.priority,
    status: task.status,
    frequency: task.frequency,
    time_slots: task.timeSlots,
    is_mandatory: task.isMandatory,
    due_date: task.dueDate,
    assigned_workers: task.assignedWorkers,
    checklist: task.checklist,
    notes: task.notes,
    farm_id: task.farm_id,
    created_at: task.created_at || task.createdAt,
    updated_at: task.updated_at || task.updatedAt,
    completed_date: task.completedDate || task.completed_date
  };
};

interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  subtitle?: string;
}

const SummaryCard = ({ title, value, icon, color, subtitle }: SummaryCardProps) => {
  const theme = useTheme();
  const paletteColor = theme.palette[color] as any;
  
  return (
    <Card elevation={0} sx={{ 
      height: '100%', 
      borderRadius: 3, 
      border: `1px solid ${alpha(paletteColor.main, 0.2)}`,
      bgcolor: alpha(paletteColor.main, 0.05),
    }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3, '&:last-child': { pb: 3 } }}>
        <Box sx={{ 
          mr: 2, 
          p: 1.5, 
          borderRadius: 2, 
          bgcolor: alpha(paletteColor.main, 0.15),
          color: paletteColor.main,
          display: 'flex'
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight="600">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {value}
          </Typography>
          {subtitle && (
             <Typography variant="caption" color="text.secondary">
               {subtitle}
             </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * FarmTasks component for managing tasks on a farm
 * Provides functionality to view, add, edit, delete, and track task completion
 * @param farmId - The ID of the farm to manage tasks for
 * @returns React component for farm task management
 */
const FarmTasks: React.FC<FarmTasksProps> = ({
  farmId,
  workers = [],
  tutorialMode = false,
  tutorialTasks = [],
  tutorialMessage,
}) => {
  const theme = useTheme();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  
  const [newTask, setNewTask] = useState<Partial<Task>>({
    title: '',
    description: '',
    category: '',
    priority: 'medium',
    status: 'pending',
    frequency: 'daily',
    timeSlots: [],
    isMandatory: false,
    dueDate: '',
    assignedTo: [],
    checklist: [],
    notes: '',
    assignedWorkers: [],
  });
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTask, setMenuTask] = useState<Task | null>(null);

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line
  }, [farmId]);

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    if (tutorialMode) {
      setTasks(tutorialTasks);
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('farm_id', farmId)
        .order('due_date', { ascending: true });
      
      if (error) throw error;
      setTasks(data || []);
    } catch (err: any) {
      console.error('Error fetching tasks:', err);
      setTasks([]);
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Opens the dialog to add a new task
   * Initializes the editing task state with default values
   */
  const handleAddTask = () => {
    if (tutorialMode) return;
    setEditingTask(null);
    setNewTask({
      title: '',
      description: '',
      category: '',
      priority: 'medium',
      status: 'pending',
      frequency: 'daily',
      timeSlots: [],
      isMandatory: false,
      dueDate: '',
      assignedTo: [],
      checklist: [],
      notes: '',
      assignedWorkers: [],
    });
    setOpenDialog(true);
  };

  /**
   * Opens the dialog to edit an existing task
   * @param task - The task object to edit
   */
  const handleEditTask = (task: Task) => {
    if (tutorialMode) return;
    setEditingTask(task);
    setNewTask(task);
    setOpenDialog(true);
  };

  /**
   * Deletes a task from the tasks list
   * @param taskId - The ID of the task to delete
   */
  const handleDeleteTask = async (taskId: string) => {
    if (tutorialMode) return;
    setError(null);
    try {
      const { error } = await supabase.from('tasks').delete().eq('id', taskId).eq('farm_id', farmId);
      if (error) throw error;
      fetchTasks();
    } catch (err: any) {
      setError(err.message || 'Failed to delete task');
    }
  };

  /**
   * Opens the worker assignment dialog for a task
   * @param task The task to assign workers to
   */
  const handleOpenAssignDialog = (task: Task) => {
    if (tutorialMode) return;
    setSelectedTask(task);
    setOpenAssignDialog(true);
  };

  /**
   * Saves the current task being edited
   * Updates existing task or adds new task to the list
   */
  const handleSaveTask = async () => {
    if (tutorialMode) return;
    setError(null);
    const now = new Date().toISOString();
    try {
      if (editingTask) {
        // Update
        const payload = toSnakeCaseTask(newTask);
        const { error } = await supabase
          .from('tasks')
          .update(payload)
          .eq('id', editingTask.id)
          .eq('farm_id', farmId);
        if (error) throw error;
      } else {
        // Add
        const payload = toSnakeCaseTask({
          ...newTask,
          farm_id: farmId,
          created_at: now,
          updated_at: now,
        });
        const { error } = await supabase
          .from('tasks')
          .insert([payload]);
        if (error) throw error;
      }
      setOpenDialog(false);
      fetchTasks();
    } catch (err: any) {
      setError(err.message || 'Failed to save task');
    }
  };

  /**
   * Handles changes to task form input fields
   * @param field - The field name to update
   * @param value - The new value for the field
   */
  const handleInputChange = (field: keyof Task, value: any) => {
    setNewTask({ ...newTask, [field]: value });
  };

  /**
   * Toggles the completion status of a checklist item
   * @param taskId - The ID of the task containing the checklist
   * @param checkItemId - The ID of the checklist item to toggle
   */
  const handleChecklistChange = (taskId: string, checkItemId: string) => {
    setTasks(tasks.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          checklist: task.checklist.map(item => 
            item.id === checkItemId ? { ...item, completed: !item.completed } : item
          ),
        };
      }
      return task;
    }));
  };

  /**
   * Handles the assignment of workers to a task
   * @param selectedWorkerIds Array of worker IDs to assign
   */
  const handleAssignWorkers = async (selectedWorkerIds: string[]) => {
    if (tutorialMode) {
      setOpenAssignDialog(false);
      return;
    }
    if (selectedTask) {
      try {
        const { error } = await supabase
          .from('tasks')
          .update({ assignedWorkers: Array.isArray(selectedWorkerIds) ? selectedWorkerIds : [] })
          .eq('id', selectedTask.id)
          .eq('farm_id', farmId);
        if (error) throw error;
        fetchTasks();
      } catch (err: any) {
        setError(err.message || 'Failed to assign workers');
      }
    }
    setOpenAssignDialog(false);
  };

  /**
   * Updates task status
   * @param taskId ID of the task to update
   * @param newStatus New status value
   */
  const handleStatusChange = async (taskId: string, newStatus: Task['status']) => {
    if (tutorialMode) return;
    setError(null);
    try {
      const completedDate = newStatus === 'completed' ? new Date().toISOString().split('T')[0] : null;
      const { error } = await supabase
        .from('tasks')
        .update({ status: newStatus, completed_date: completedDate })
        .eq('id', taskId)
        .eq('farm_id', farmId);
      if (error) throw error;
      fetchTasks();
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    }
  };

  /**
   * Gets color for status chip based on status value
   * @param status Task status
   * @returns MUI color value
   */
  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'primary';
      case 'on-hold': return 'warning';
      default: return 'default';
    }
  };

  /**
   * Gets color for priority chip based on priority value
   * @param priority Task priority
   * @returns MUI color value
   */
  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  };

  /**
   * Gets icon for status
   * @param status Task status
   * @returns React icon element
   */
  const getStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon fontSize="small" />;
      case 'in-progress': return <ScheduleIcon fontSize="small" />;
      case 'on-hold': return <WarningIcon fontSize="small" />;
      default: return <AssignmentIcon fontSize="small" />;
    }
  };

  /**
   * Gets worker names for display
   * @param workerIds Array of worker IDs
   * @returns Array of worker names
   */
  const getWorkerNames = (workerIds: string[]) => {
    return (workerIds && Array.isArray(workerIds) ? workerIds : []).map(id => {
      const worker = workers.find(w => w.id === id);
      return worker ? worker.name : 'Unknown Worker';
    });
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, task: Task) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuTask(task);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuTask(null);
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          task.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const stats = useMemo(() => {
    const totalTasks = tasks.length;
    const pendingTasks = tasks.filter(t => t.status === 'pending').length;
    const inProgressTasks = tasks.filter(t => t.status === 'in-progress').length;
    const completedTasks = tasks.filter(t => t.status === 'completed').length;
    const highPriorityTasks = tasks.filter(t => t.priority === 'high' || t.priority === 'urgent').length;
    return { totalTasks, pendingTasks, inProgressTasks, completedTasks, highPriorityTasks };
  }, [tasks]);

  const getProgress = (task: Task) => {
    if (!task.checklist || task.checklist.length === 0) return 0;
    const completed = task.checklist.filter(i => i.completed).length;
    return Math.round((completed / task.checklist.length) * 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      {tutorialMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {tutorialMessage || 'This tutorial task board is read-only. Use it to understand how field operations can be planned before you create real farm tasks.'}
        </Alert>
      )}
      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="Total Tasks" value={stats.totalTasks} icon={<TaskIcon />} color="primary" subtitle={`${stats.highPriorityTasks} High Priority`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="Pending" value={stats.pendingTasks} icon={<PendingActionsIcon />} color="warning" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="In Progress" value={stats.inProgressTasks} icon={<WorkHistoryIcon />} color="info" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="Completed" value={stats.completedTasks} icon={<AssignmentTurnedInIcon />} color="success" />
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <TextField
                placeholder="Search tasks..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                }}
                sx={{ 
                    '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        width: { xs: '100%', sm: 300 } 
                    } 
                }}
            />
            <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: 2 }}
                >
                    <MenuItem value="all">All Status</MenuItem>
                    {taskStatuses.map(status => (
                        <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
             <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: 2 }}
                >
                    <MenuItem value="all">All Priorities</MenuItem>
                    {taskPriorities.map(priority => (
                        <MenuItem key={priority.value} value={priority.value}>{priority.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newView) => { if(newView) setViewMode(newView); }}
                size="small"
                sx={{ bgcolor: 'background.paper' }}
            >
                <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
                <ToggleButton value="list"><ViewListIcon /></ToggleButton>
            </ToggleButtonGroup>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTask}
          disabled={tutorialMode}
          sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
        >
          {tutorialMode ? 'Read-Only Tutorial Plan' : 'Add Task'}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <Grid container spacing={3}>
              {filteredTasks.map((task) => (
                <Grid item xs={12} sm={6} md={4} key={task.id}>
                  <StyledCard>
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <PriorityChip 
                            label={task.priority} 
                            priority={task.priority}
                            size="small"
                          />
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                            {task.category}
                          </Typography>
                        </Box>
                        {!tutorialMode && (
                          <IconButton size="small" onClick={(e) => handleMenuClick(e, task)}>
                            <MoreVertIcon />
                          </IconButton>
                        )}
                      </Box>
                      
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {task.title}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: '40px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {task.description}
                      </Typography>
                      
                      {task.checklist && task.checklist.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">Progress</Typography>
                            <Typography variant="caption">{getProgress(task)}%</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={getProgress(task)} sx={{ borderRadius: 1, height: 6 }} />
                        </Box>
                      )}
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                             {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date'}
                          </Typography>
                        </Box>
                         <StatusChip 
                            label={task.status.replace('-', ' ')} 
                            status={task.status}
                            size="small"
                          />
                      </Box>
                      
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                           {(task.assignedWorkers && Array.isArray(task.assignedWorkers) ? task.assignedWorkers : []).map(workerId => {
                              const worker = workers.find(w => w.id === workerId);
                              return (
                                <Tooltip key={workerId} title={worker?.name || 'Unknown'}>
                                  <Avatar>{worker?.name.charAt(0) || 'U'}</Avatar>
                                </Tooltip>
                              );
                            })}
                        </AvatarGroup>
                        {!tutorialMode && (!task.assignedWorkers || task.assignedWorkers.length === 0) && (
                           <Button size="small" startIcon={<PersonIcon />} onClick={() => handleOpenAssignDialog(task)}>
                             Assign
                           </Button>
                        )}
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Table>
                <TableHead sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)' }}>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTasks.map((task) => (
                    <TableRow key={task.id} hover onClick={() => handleEditTask(task)} sx={{ cursor: tutorialMode ? 'default' : 'pointer' }}>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{task.title}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>{task.description}</Typography>
                      </TableCell>
                      <TableCell>{task.category}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <AccessTimeIcon fontSize="small" color="action" />
                           <Typography variant="body2">
                            {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}
                           </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <PriorityChip 
                          label={task.priority} 
                          priority={task.priority}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <StatusChip 
                          label={task.status.replace('-', ' ')} 
                          status={task.status}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <AvatarGroup max={3} sx={{ justifyContent: 'flex-start' }}>
                            {(task.assignedWorkers && Array.isArray(task.assignedWorkers) ? task.assignedWorkers : []).map(workerId => {
                              const worker = workers.find(w => w.id === workerId);
                              return (
                                <Tooltip key={workerId} title={worker?.name || 'Unknown'}>
                                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem' }}>
                                    {worker?.name.charAt(0) || 'U'}
                                  </Avatar>
                                </Tooltip>
                              );
                            })}
                        </AvatarGroup>
                      </TableCell>
                      <TableCell>
                        {(task.checklist && Array.isArray(task.checklist) && task.checklist.length > 0) ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={getProgress(task)} sx={{ width: 60, height: 6, borderRadius: 1 }} />
                            <Typography variant="caption">{getProgress(task)}%</Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!tutorialMode && (
                          <IconButton size="small" onClick={(e) => handleMenuClick(e, task)}>
                            <MoreVertIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Task Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: '12px', minWidth: '160px' }
        }}
      >
        <MenuItem onClick={() => { handleEditTask(menuTask!); handleMenuClose(); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={() => { handleOpenAssignDialog(menuTask!); handleMenuClose(); }}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          Assign Workers
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleDeleteTask(menuTask!.id); handleMenuClose(); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTask ? 'Edit Task' : 'Add New Task'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={newTask.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={newTask.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={newTask.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
              >
                {taskCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Priority"
                value={newTask.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
              >
                {taskPriorities.map((priority) => (
                  <MenuItem key={priority.value} value={priority.value}>
                    {priority.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Due Date"
                value={newTask.dueDate}
                onChange={(e) => handleInputChange('dueDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={newTask.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                {taskStatuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Frequency"
                value={newTask.frequency}
                onChange={(e) => handleInputChange('frequency', e.target.value)}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newTask.isMandatory}
                    onChange={(e) => handleInputChange('isMandatory', e.target.checked)}
                  />
                }
                label="Mandatory Task"
              />
            </Grid>
            <Grid item xs={12}>
              {newTask.frequency === 'daily' && (
                <TextField
                  fullWidth
                  label="Time Slots (comma-separated, e.g., 09:00,14:00,18:00)"
                  value={newTask.timeSlots?.join(',') || ''}
                  onChange={(e) => handleInputChange('timeSlots', e.target.value.split(','))}
                  helperText="Enter times when this task should be performed daily"
                />
              )}              
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Checklist
              </Typography>
              {editingTask && (Array.isArray(editingTask.checklist) ? editingTask.checklist : []).map((item) => (
                <FormControlLabel
                  key={item.id}
                  control={
                    <Checkbox
                      checked={item.completed}
                      onChange={() => handleChecklistChange(editingTask.id, item.id)}
                    />
                  }
                  label={item.text}
                />
              ))}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={newTask.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveTask} variant="contained" color="primary">
            {editingTask ? 'Update' : 'Add'} Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Workers Dialog */}
      <Dialog 
        open={openAssignDialog} 
        onClose={() => setOpenAssignDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Workers to Task</DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {selectedTask.title}
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Select Workers:
              </Typography>
              
              <FormControl fullWidth sx={{ mt: 1 }}>
                <Select
                  multiple
                  value={selectedTask && Array.isArray(selectedTask.assignedWorkers) ? selectedTask.assignedWorkers : []}
                  onChange={(e) => {
                    const value = Array.isArray(e.target.value) ? e.target.value : [];
                    if (selectedTask) {
                      setSelectedTask({
                        ...selectedTask,
                        assignedWorkers: value
                      });
                    }
                  }}
                  input={<OutlinedInput />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const worker = workers.find(w => w.id === value);
                        return (
                          <Chip 
                            key={value} 
                            label={worker?.name || 'Unknown'} 
                            size="small" 
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {workers.map((worker) => (
                    <MenuItem key={worker.id} value={worker.id}>
                      <Checkbox checked={(selectedTask && Array.isArray(selectedTask.assignedWorkers) ? selectedTask.assignedWorkers : []).indexOf(worker.id) > -1} />
                      <ListItemText 
                        primary={worker.name} 
                        secondary={`${worker.role} (${worker.specialization.join(', ')})`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Worker specialization recommendation */}
              {selectedTask.category && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Recommended Workers:
                  </Typography>
                  {workers
                    .filter(worker => 
                      worker.specialization.some(spec => 
                        spec.toLowerCase().includes(selectedTask.category.toLowerCase()) ||
                        selectedTask.category.toLowerCase().includes(spec.toLowerCase())
                      )
                    )
                    .map(worker => (
                      <Chip 
                        key={worker.id}
                        label={worker.name}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                        onClick={() => {
                          if (selectedTask) {
                            const isAlreadyAssigned = selectedTask.assignedWorkers.includes(worker.id);
                            setSelectedTask({
                              ...selectedTask,
                              assignedWorkers: isAlreadyAssigned
                                ? selectedTask.assignedWorkers.filter(id => id !== worker.id)
                                : [...selectedTask.assignedWorkers, worker.id]
                            });
                          }
                        }}
                      />
                    ))}
                  {workers.filter(worker => 
                    worker.specialization.some(spec => 
                      spec.toLowerCase().includes(selectedTask.category.toLowerCase()) ||
                      selectedTask.category.toLowerCase().includes(spec.toLowerCase())
                    )
                  ).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No workers with matching specialization found.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => handleAssignWorkers(selectedTask?.assignedWorkers || [])}
            variant="contained" 
            color="primary"
          >
            Assign Workers
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FarmTasks;
